import { Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { CognitoIdentityServiceProvider } from 'aws-sdk';
import { GroupType } from 'aws-sdk/clients/cognitoidentityserviceprovider';
import React, { useEffect, useState } from 'react';
import GenericTable from '../HealthCareReview/GenericTable';
import AdminService from './AdminService';
import CreateGroupDialog from './CreateGroupDialog';
import DeleteGroupDialog from './DeleteGroupDialog';
import UserDialog from './UserDialog';
interface DeleteGroupAction {
    group: CognitoIdentityServiceProvider.GroupType;
}

export default function AdminPage() {

    const [deleteGroupAction, setDeleteGroupAction] = useState<DeleteGroupAction | null>(null);

    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);

    const [selectedTab, setSelectedTab] = useState('users');
    const handleTabChange = async (event: React.ChangeEvent<{}>, newValue: string) => {
        setSelectedTab(newValue);
    }

    const [selectedUser, setSelectedUser] = useState<CognitoIdentityServiceProvider.UserType | null>();

    const [groups, setGroups] = useState<CognitoIdentityServiceProvider.GroupType[]>([]);
    const [users, setUsers] = useState<CognitoIdentityServiceProvider.UserType[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>(''); // Any error message from fetch / save

    useEffect(() => {
        const createDefaultGroupsIfMissing = async (pGroups: CognitoIdentityServiceProvider.GroupListType) => {
            let reloadRequired = false;
            if (!pGroups.find(g => g.GroupName === 'ActiveEpisodes')) {
                await AdminService.createGroup('ActiveEpisodes', 'Provides access to the Active Episodes grid');
                reloadRequired = true;
            }
            if (!pGroups.find(g => g.GroupName === 'EhwcQueue')) {
                await AdminService.createGroup('EhwcQueue', 'Provides access to the EHWC Queue grid');
                reloadRequired = true;
            }
            if (!pGroups.find(g => g.GroupName === 'AllEpisodes')) {
                await AdminService.createGroup('AllEpisodes', 'Provides access to the All Episodes grid');
                reloadRequired = true;
            }
            // if (!pGroups.find(g => g.GroupName === 'MostSevereTestResult')) {
            //     await AdminService.createGroup('MostSevereTestResult', 'Provides access to the Episoded With Test Result grid');
            //     reloadRequired = true;
            // }
            if (!pGroups.find(g => g.GroupName === 'MassAsymptomaticTesting')) {
                await AdminService.createGroup('MassAsymptomaticTesting', 'Provides access to the Mass Asymtomatic Testing grid');
                reloadRequired = true;
            }
            return reloadRequired;
        }

        const loadAllGroups = async () => {
            const lGroups = await AdminService.listGroups();
            // if (await createDefaultGroupsIfMissing(lGroups)) {
            //     setGroups(await AdminService.listGroups());
            // } else {
            setGroups(lGroups);
            // }
        }

        const loadUsers = async () => {
            setIsLoading(true);
            try {
                const listUsers = await AdminService.listUsers();
                if (listUsers) {
                    setUsers(listUsers.map(u => {
                        return {
                            ...u,
                            email: getEmail(u)
                        };
                    }));
                }
            } catch (err) {
                setError('Error listing users');
            } finally {
                setIsLoading(false);
            }
        }

        loadAllGroups();
        loadUsers();
    }, []);

    const getEmail = (user: CognitoIdentityServiceProvider.UserType) => {
        return user.Attributes?.find(a => a.Name === 'email')?.Value || '';
    }

    const handleGroupSave = async (group: GroupType) => {
        if (group.GroupName) {
            await AdminService.createGroup(group.GroupName, group.Description, group.Precedence);
            setGroups(groups.concat(group));
        }
    }

    const handleGroupDelete = async (group: GroupType) => {
        if (group.GroupName) {
            await AdminService.deleteGroup(group.GroupName);
            setGroups(groups.filter(g => g.GroupName !== group.GroupName));
        }
    }

    const handleAddToGroup = async (user: CognitoIdentityServiceProvider.UserType, groupName: string) => {
        if (user.Username && groupName) {
            try {
                await AdminService.addUserToGroup(user.Username, groupName);
            } catch (err) {
                await AdminService.createGroup(groupName);
                await AdminService.addUserToGroup(user.Username, groupName);
            }
        }
    }

    const handleRemoveFromGroup = (user: CognitoIdentityServiceProvider.UserType, groupName: string) => {
        if (user.Username && groupName) {
            AdminService.removeUserFromGroup(user.Username, groupName);
        }
    }

    return (
        <React.Fragment>
            <p style={{ color: "red" }} hidden={!error}>An error has occurred: {error}</p>
            <Box mb={1}>
                <Typography variant="h5">Admin Console</Typography>
            </Box>

            <Paper square>
                <Tabs
                    value={selectedTab}
                    indicatorColor="primary"
                    textColor="primary"
                    onChange={handleTabChange}
                    aria-label="disabled tabs example"
                >
                    <Tab value="users" label="Users" />
                    {/*<Tab value="groups" label="Groups" />*/}
                </Tabs>
            </Paper>

            {selectedTab === 'users' &&
                <GenericTable<CognitoIdentityServiceProvider.UserType, {}>
                    title="Users"
                    columns={[
                        {
                            title: "Email",
                            field: "email",
                            render: u => getEmail(u),
                            defaultSort: "asc",
                            width: 100
                        },
                        {
                            title: "Enabled",
                            field: "Enabled",
                            render: o => o.Enabled === true ? 'true' : 'false',
                            width: 100
                        },
                        {
                            title: "Updated",
                            field: "UserLastModifiedDate",
                            width: 100
                        },
                        {
                            title: "Created",
                            field: "UserCreateDate",
                            width: 100
                        },
                    ]}
                    data={users}
                    isLoading={isLoading}
                    onRowClick={(_, rowData) => rowData && setSelectedUser(rowData)}
                    grouping={true}
                    optionOverrides={{
                        filtering: true
                    }}
                />
            }

            {selectedTab === 'groups' &&
                <GenericTable<CognitoIdentityServiceProvider.GroupType, {}>
                    title={
                        <React.Fragment>
                            <Typography variant="h6">
                                <Box mr={2} display="inline">Users</Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowCreateGroupDialog(true)}
                                >
                                    Create Group
                                </Button>
                            </Typography>
                        </React.Fragment>
                    }
                    columns={[
                        {
                            title: "Group",
                            field: "GroupName",
                            defaultSort: "asc",
                            width: 100
                        },
                        {
                            title: "Description",
                            field: "Enabled",
                            width: 100
                        },
                        /*{
                            title: "Precedence",
                            field: "Precedence",
                            render: o => o.Precedence,
                            width: 100
                        },*/
                        {
                            title: "Updated",
                            field: "LastModifiedDate",
                            render: o => o.LastModifiedDate ? o.LastModifiedDate : 'Now',
                            width: 100
                        },
                        {
                            title: "Created",
                            field: "CreationDate",
                            render: o => o.CreationDate ? o.CreationDate : 'Now',
                            width: 100
                        }
                    ]}
                    data={groups}
                    isLoading={isLoading}
                    // onRowClick={(_, rowData) => rowData && setShowCreateGroupDialog(true)}
                    grouping={true}
                    customActions={[
                        {
                            icon: () => <DeleteForeverIcon />,
                            tooltip: 'Delete',
                            onClick: (_, data) => {
                                if (data) {
                                    setDeleteGroupAction({ group: data as GroupType });
                                }
                            },
                        },
                    ]}
                />
            }

            {groups && selectedUser &&
                <UserDialog
                    user={selectedUser}
                    allGroups={groups}
                    onClose={() => setSelectedUser(null)}
                    onAddToGroup={handleAddToGroup}
                    onRemoveFromGroup={handleRemoveFromGroup}
                />
            }

            {showCreateGroupDialog &&
                <CreateGroupDialog
                    onSave={handleGroupSave}
                    onClose={() => setShowCreateGroupDialog(false)}
                />
            }

            {deleteGroupAction &&
                <DeleteGroupDialog
                    group={deleteGroupAction.group}
                    onDelete={handleGroupDelete}
                    onClose={() => setDeleteGroupAction(null)}
                />
            }
        </React.Fragment>
    );
}
